import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api.js";
import { Skeleton } from "@bongeunsa/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import type { MainPage } from "@bongeunsa/db";

const PUBLIC_BASE = import.meta.env.DEV ? "http://localhost:5173" : "https://bongeunsa.app";

export function MainPagesPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<MainPage | null>(null);
  const [selectedPage, setSelectedPage] = useState<MainPage | null>(null);

  const { data: pages, isLoading } = useQuery({
    queryKey: ["main-pages"],
    queryFn: () => api.get<MainPage[]>("/api/main-pages"),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      api.post<MainPage>("/api/main-pages", { name }),
    onSuccess: () => {
      setCreating(false);
      setNewName("");
      qc.invalidateQueries({ queryKey: ["main-pages"] });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/main-pages/${id}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["main-pages"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/main-pages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["main-pages"] });
      setDeleteTarget(null);
    },
  });

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* ── Left: list ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">메인페이지</h1>
          <button
            onClick={() => setCreating(true)}
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            + 생성
          </button>
        </div>

        {creating && (
          <div className="mb-4 flex gap-2 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createMutation.mutate(newName)}
              placeholder="페이지 이름"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
            />
            <button
              onClick={() => createMutation.mutate(newName)}
              disabled={!newName.trim() || createMutation.isPending}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              만들기
            </button>
            <button
              onClick={() => setCreating(false)}
              className="rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
            >
              취소
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        ) : !pages?.length ? (
          <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
            <p className="text-gray-400">아직 메인페이지가 없습니다.</p>
            <button
              onClick={() => setCreating(true)}
              className="mt-3 text-sm font-medium text-brand-600 hover:underline"
            >
              + 첫 번째 페이지 만들기
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {pages.map((page) => (
              <div
                key={page.id}
                onClick={() => setSelectedPage(page)}
                className={`flex cursor-pointer items-center gap-4 rounded-xl bg-white px-5 py-4 shadow-sm ring-1 transition-colors ${
                  selectedPage?.id === page.id
                    ? "ring-brand-400 bg-brand-50/30"
                    : "ring-black/5 hover:ring-brand-200"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate font-semibold text-gray-900">{page.name}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(page.updated_at).toLocaleString("ko-KR")}
                  </p>
                </div>

                {page.is_active ? (
                  <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                    액티브
                  </span>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); activateMutation.mutate(page.id); }}
                    disabled={activateMutation.isPending}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    액티브로
                  </button>
                )}

                <Link
                  to={`/main-pages/${page.id}/edit`}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  편집
                </Link>

                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(page); }}
                  className="rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600"
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        )}

        <ConfirmDialog
          open={!!deleteTarget}
          title="메인페이지 삭제"
          description={`"${deleteTarget?.name}" 페이지를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`}
          confirmLabel="삭제"
          danger
          onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      </div>

      {/* ── Right: selected page preview ─────────────────────── */}
      <div className="hidden lg:flex w-[380px] shrink-0 flex-col border-l border-gray-100 bg-gray-50">
        {selectedPage ? (
          <>
            <div className="border-b border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-bold text-gray-900">{selectedPage.name}</p>
                {selectedPage.is_active && (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">액티브</span>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-3">
                수정일: {new Date(selectedPage.updated_at).toLocaleString("ko-KR")}
              </p>
              <div className="flex gap-2">
                <Link
                  to={`/main-pages/${selectedPage.id}/edit`}
                  className="flex-1 rounded-xl bg-brand-500 px-3 py-2 text-center text-xs font-semibold text-white hover:bg-brand-600"
                >
                  편집하기
                </Link>
                <a
                  href={`${PUBLIC_BASE}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-white"
                >
                  사이트 보기 ↗
                </a>
              </div>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <p className="mb-2 text-xs font-medium text-gray-400">모바일 미리보기</p>
              <div className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-black/10 mx-auto" style={{ width: 320, height: 560 }}>
                <iframe key={selectedPage.id} src={`${PUBLIC_BASE}/?preview=${selectedPage.id}`} width={320} height={560} className="block" title="미리보기" />
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-gray-300">← 페이지를 선택하면 미리보기가 표시됩니다</p>
          </div>
        )}
      </div>
    </div>
  );
}
